import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import Grid from '@mui/material/Grid2';
import DonutChart from '@ui/components/DonutChart/DonutChart';
import { useTranslation } from 'react-i18next';
import { Box, Button, Typography, useTheme } from '@mui/material';
import PageTabs from '@ui/components/common/PageTabs';
import { Edit2 } from 'iconsax-react';
import { useEffect, useState } from 'react';
import EditDashboardDialog from './EditDashboardDialog';
import BarChart from '@ui/components/BarChart';
import { useParams } from 'react-router-dom';
import { useAppDispatch } from '@application/store/useStore';
import { getEventFormStepFields, getEventFormSteps, getEventFormStepsAndFields } from '@application/features/eventDashboard/eventDashboardThunk';
import { useSelector } from 'react-redux';
import { EventTypeEnum } from '@application/enums/event-type-enum';
import { hasPermission } from '@application/helpers/permission-helper';
import { between } from '@ui/style/style';
const DashboardTab = () => {
    const { t } = useTranslation();
    const theme = useTheme();
    const [activeStep, setActiveStep] = useState(0);
    const [openEditDialog, setOpenEditDialog] = useState(false);
    const currentUserRoles = useSelector((state) => state.auth.currentUser?.role);
    const isCollaborator = Array.isArray(currentUserRoles) && (currentUserRoles.includes('Collaborator') || currentUserRoles.includes('TransportManager'));
    const loggedInUserPermissions = useSelector((state) => state.auth.currentUser?.permissions || []);
    const canUpdateEvents = hasPermission(loggedInUserPermissions, 'Permissions.VdkEvents.Update');
    const { eventId } = useParams();
    const dispatch = useAppDispatch();
    const { formSteps, formQuantitableFields, formStepsAndFields, isFormUpdated } = useSelector((state) => state.eventDashboard);
    const { eventType } = useSelector((state) => state.f1Integration);
    const familyFormSteps = [
        t('familyFest.personal'),
        t('familyFest.adults'),
        t('familyFest.children'),
        t('familyFest.transportation'),
        t('familyFest.additional')
    ];
    useEffect(() => {
        if (eventType == EventTypeEnum.Family) {
        }
        dispatch(getEventFormSteps(eventId))
            .unwrap()
            .then((res) => {
            if (res && res.length > 0 && res[0].stepId) {
                dispatch(getEventFormStepFields(res[activeStep].stepId));
            }
        });
    }, [eventId, isFormUpdated]);
    const handleOpenEditDialog = () => {
        dispatch(getEventFormStepsAndFields(eventId))
            .unwrap()
            .then((res) => {
            if (res && res.length > 0 && res[0].stepId) {
                setOpenEditDialog(true);
            }
        });
    };
    const handleCloseEditDialog = () => {
        setOpenEditDialog(false);
    };
    const sortedSteps = (formSteps ?? [])
        .map((step) => ({ ...step }))
        .sort((a, b) => a.sortOrder - b.sortOrder);
    const stepsData = sortedSteps.map((step) => {
        const fields = (formQuantitableFields ?? []).filter((f) => f.stepId === step.stepId);
        return {
            stepId: step.stepId,
            label: step.stepName,
            formQuantitableFields: fields
        };
    });
    return (_jsxs(_Fragment, { children: [_jsxs(Box, { children: [_jsxs(Box, { sx: { ...between, flexWrap: 'wrap', gap: 1 }, children: [_jsx(Box, { width: { xs: '100%', sm: 'calc(100% - 150px)', md: 'auto' }, children: _jsx(PageTabs, { tabs: stepsData.map((step, stepIndex) => ({
                                        label: `${stepIndex + 1}. ${step.label}`,
                                        value: stepIndex
                                    })), value: activeStep, onChange: (_, newValue) => {
                                        setActiveStep(newValue);
                                        const stepId = sortedSteps[newValue]?.stepId;
                                        if (stepId) {
                                            dispatch(getEventFormStepFields(stepId));
                                        }
                                    } }) }), _jsx(Button, { size: 'small', variant: 'contained', color: 'primary', startIcon: _jsx(Edit2, {}), onClick: handleOpenEditDialog, disabled: isCollaborator || !canUpdateEvents, children: t('events.eventDetails.dashboardTab.editDashboard') })] }), _jsxs(Grid, { container: true, spacing: 2, children: [_jsx(Grid, { size: { xs: 12 } }), stepsData[activeStep]?.formQuantitableFields?.length > 0 ? (stepsData[activeStep].formQuantitableFields.map((field, fieldIndex) => {
                                const options = field.optionSelected ?? [];
                                const nonZeroOptions = options.filter((opt) => opt.value > 0);
                                const labels = nonZeroOptions.map((opt) => opt.key);
                                const values = nonZeroOptions.map((opt) => opt.value);
                                const barChartData = {
                                    labels: [field.fieldLabel],
                                    datasets: nonZeroOptions.map((opt, idx) => ({
                                        label: opt.key,
                                        data: [opt.value],
                                        backgroundColor: [
                                            theme.palette.primary.light,
                                            theme.palette.success.main,
                                            theme.palette.info.light,
                                            theme.palette.warning.light,
                                            theme.palette.error.light
                                        ][idx % 5],
                                        borderColor: [
                                            theme.palette.primary.dark,
                                            theme.palette.success.dark,
                                            theme.palette.info.dark,
                                            theme.palette.warning.dark,
                                            theme.palette.error.dark
                                        ][idx % 5]
                                    }))
                                };
                                const chartData = {
                                    labels,
                                    values,
                                    colors: labels.map((_, idx) => [
                                        theme.palette.primary.light,
                                        theme.palette.success.main,
                                        theme.palette.info.light,
                                        theme.palette.warning.light,
                                        theme.palette.error.light
                                    ][idx % 5])
                                };
                                return (_jsx(Grid, { size: { xs: 12, sm: 6, md: 4 }, children: _jsxs(Box, { sx: {
                                            backgroundColor: '#F9FAFB',
                                            border: '1px solid #919EAB33',
                                            borderRadius: theme.spacing(4),
                                            padding: theme.spacing(2.5),
                                            display: 'flex',
                                            flexDirection: 'column',
                                            gap: theme.spacing(2),
                                            height: '100%'
                                            // minHeight: '300px',
                                            // maxHeight: '400px'
                                        }, children: [_jsx(Typography, { variant: 'h6', sx: {
                                                    flexShrink: 0
                                                }, children: field.fieldLabel }), _jsx(Box, { sx: {
                                                    flex: 1,
                                                    display: 'flex',
                                                    justifyContent: 'center',
                                                    alignItems: 'center',
                                                    minHeight: 0,
                                                    overflow: 'hidden',
                                                    position: 'relative'
                                                }, children: options.every((opt) => opt.value === 0) ? (_jsx(Typography, { variant: 'body2', color: theme.palette.text.secondary, children: t('events.noData') })) : (_jsx(Box, { sx: {
                                                        width: '100%',
                                                        height: '100%',
                                                        display: 'flex',
                                                        flexDirection: 'column',
                                                        justifyContent: 'center',
                                                        alignItems: 'center',
                                                        overflow: 'hidden'
                                                    }, children: options.length > 3 || field.fieldType === 9 ? (_jsx(Box, { sx: { width: '100%', height: '100%', maxHeight: '250px' }, children: _jsx(BarChart, { data: barChartData, height: 200 }) })) : (_jsx(Box, { sx: { width: '100%', height: '100%', maxHeight: '250px' }, children: _jsx(DonutChart, { data: chartData, ringThickness: '85%', height: 200 }) })) })) })] }) }, field.fieldId));
                            })) : (_jsx(Grid, { size: { xs: 12 }, children: _jsx(Box, { sx: {
                                        display: 'flex',
                                        justifyContent: 'center',
                                        alignItems: 'center',
                                        minHeight: '200px',
                                        width: '100%'
                                    }, children: _jsx(Typography, { variant: 'body2', color: theme.palette.common.black, children: t('events.noData') }) }) }))] })] }), _jsx(EditDashboardDialog, { open: openEditDialog, onClose: handleCloseEditDialog, formQuantitableFields: formStepsAndFields, eventId: eventId })] }));
};
export default DashboardTab;
